import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlossPiece } from "@/components/conjugation/GlossPiece";

const CHIP = "text-[10px] uppercase tracking-widest font-bold rounded px-2 py-1 border";

/** Dos oraciones four-finger unidas por un conector. Los huecos van en orden:
 *  verbo 1 · [conector, solo en nivel 3] · verbo 2 */
export const LinkedPrompt = forwardRef(({ linked, gloss, level, hint, hint2, options,
                                          value, onChange, value2, onChange2,
                                          connector, onConnector, disabled, onEnter, onHelp }, ref) => {
  const { t } = useI18n();
  const box = "inline-flex w-auto min-w-[7.5rem] text-center text-lg font-semibold bg-white " +
              "border-b-2 border-orange-300 rounded-none px-2 py-1 focus-visible:ring-0 " +
              "focus-visible:border-orange-500";
  const izq = linked.left || {};
  const der = linked.right || {};

  const campo = (segundo) => (
    <Input key={segundo ? "v2" : "v1"} ref={segundo ? undefined : ref}
           value={segundo ? value2 : value} disabled={disabled}
           onChange={(e) => (segundo ? onChange2 : onChange)(e.target.value)}
           onKeyDown={(e) => { if (e.key === "Enter") onEnter(); }}
           className={box} placeholder={t("cj_verb_blank")}
           data-testid={segundo ? "linked-input-2" : "practice-input"} />
  );

  const conectorNode = linked.pick_connector ? (
    <Select value={connector} onValueChange={onConnector} disabled={disabled}>
      <SelectTrigger className="w-auto min-w-[10rem] inline-flex bg-white border-b-2 border-violet-300
                                border-x-0 border-t-0 rounded-none text-base font-semibold"
                     data-testid="linked-connector-select">
        <SelectValue placeholder={t("cj_pick_connector")} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {(options || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.text}</SelectItem>)}
      </SelectContent>
    </Select>
  ) : (
    <GlossPiece gloss={gloss?.connector} text={linked.connector} onUse={onHelp}
                testId="gloss-connector" className="text-emerald-700 font-medium" />
  );

  return (
    <div className="mb-2" data-testid="linked-prompt">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-3 text-lg leading-relaxed text-slate-800">
        <GlossPiece gloss={gloss?.left?.marker} text={izq.marker} onUse={onHelp}
                    testId="gloss-left-marker" className="text-sky-700 font-medium" />
        <GlossPiece gloss={gloss?.left?.subject} text={izq.subject} onUse={onHelp}
                    testId="gloss-left-subject" className="text-violet-700 font-medium" />
        {campo(false)}
        <span className="text-slate-700">
          <GlossPiece gloss={gloss?.left?.info} text={izq.info} onUse={onHelp}
                      testId="gloss-left-info" />{(linked.sep || " ").trim()}
        </span>
        {conectorNode}
        {!!(linked.after || "").trim() && (
          <span className="text-slate-700 -ml-2">{(linked.after || "").trim()}</span>
        )}
        {der.marker && (
          <GlossPiece gloss={gloss?.right?.marker} text={der.marker} onUse={onHelp}
                      testId="gloss-right-marker" className="text-sky-700 font-medium" />
        )}
        <GlossPiece gloss={gloss?.right?.subject} text={der.subject} onUse={onHelp}
                    testId="gloss-right-subject" className="text-violet-700 font-medium" />
        {campo(true)}
        <span className="text-slate-700">
          <GlossPiece gloss={gloss?.right?.info} text={der.info} onUse={onHelp}
                      testId="gloss-right-info" />.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <span className={`${CHIP} text-orange-600 bg-orange-50 border-orange-200`} data-testid="linked-hint-1">
          1 · {hint}
        </span>
        <span className={`${CHIP} text-orange-600 bg-orange-50 border-orange-200`} data-testid="linked-hint-2">
          2 · {hint2}
        </span>
        {!linked.same_tense && (
          <span className={`${CHIP} text-violet-600 bg-violet-50 border-violet-200`}>
            {t("cj_two_tenses")}
          </span>
        )}
      </div>
    </div>
  );
});
